import { createContext, useContext, type ReactNode } from 'react'
import type {
  UseTwilioChatOptions,
  UseTwilioChatReturn,
} from '../types/chat.types'
import { useTwilioChat } from '../hooks/useTwilioChat'

const ChatContext = createContext<UseTwilioChatReturn | null>(null)

export interface ChatProviderProps {
  children: ReactNode
  config?: UseTwilioChatOptions
}

export const ChatProvider = ({ children, config }: ChatProviderProps) => {
  try {
    const chat = useTwilioChat(config)
    return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>
  } catch (error) {
    // If hook throws during initialization, provide a minimal context
    console.error('ChatProvider initialization error:', error)
    const errorContext: UseTwilioChatReturn = {
      client: null,
      conversation: null,
      messages: [],
      loading: false,
      error:
        error instanceof Error ? error.message : 'Failed to initialize chat',
      sendMessage: async () => {
        throw new Error('Chat not initialized')
      },
      beginTyping: () => {},
      endTyping: () => {},
      typingParticipant: null,
      participantDisplayName: null,
      participantAvatarUrl: null,
      currentUserDisplayName: null,
    }
    return (
      <ChatContext.Provider value={errorContext}>
        {children}
      </ChatContext.Provider>
    )
  }
}

export const useChat = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider.')
  }
  return context
}
